/** "Linked from" surface. Shows documents whose markdown body
 *  references this entity via a [[EntityName]] wiki-link, sourced
 *  from the wiki_backlinks materialized index. Pass either a
 *  resolved entity (type+id) or a raw name. */
import { useEffect, useState } from "react";
import { Link2 } from "lucide-react";
import { api, type WikiBacklinkRow } from "../lib/api";

const SOURCE_LABEL: Record<string, string> = {
  node_note: "Note",
  conversation_summary: "Thread",
  recommendation: "Recommendation",
  daily_reflection: "Reflection",
};

export function WikiBacklinks({
  entityType,
  entityId,
  name,
}: {
  entityType?: string;
  entityId?: string;
  name?: string;
}) {
  const [rows, setRows] = useState<WikiBacklinkRow[] | null>(null);

  useEffect(() => {
    if (!((entityType && entityId) || name)) return;
    api
      .wikiBacklinks({ entity_type: entityType, entity_id: entityId, name })
      .then((d) => setRows(d.backlinks))
      .catch(() => setRows([]));
  }, [entityType, entityId, name]);

  if (!rows || rows.length === 0) return null;

  return (
    <section className="px-4 sm:px-6 py-4 border-t border-slate-200 dark:border-slate-700">
      <h4 className="flex items-center gap-1.5 text-xs uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400 mb-2">
        <Link2 className="w-3.5 h-3.5" />
        Linked from {rows.length}{" "}
        {rows.length === 1 ? "place" : "places"}
      </h4>
      <ul className="space-y-2">
        {rows.slice(0, 12).map((b) => (
          <li
            key={`${b.source_kind}:${b.source_id}`}
            className="text-xs text-slate-700 dark:text-slate-200"
          >
            <span className="inline-block rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500 dark:text-slate-400 mr-2">
              {SOURCE_LABEL[b.source_kind] ?? b.source_kind}
            </span>
            {b.source_title && (
              <span className="font-medium">{b.source_title} </span>
            )}
            <span className="text-slate-500 dark:text-slate-400">
              {(b.preview || "").slice(0, 120)}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
